import liteguid from "lite-guid";
type State = {
	contextId: string;
	contextType: string;
	subContextId: string;
	subContextType: string;
	testGroups: any;
	cookieDomain: string;
	// For multidomain tracking, use .domain.tld
	userCookieName: string;
	sessionCookieName: string;
	orderId: string | null | undefined;
};

class TrackingState {
	state: State;
	CONTEXT_TYPE_VIEW = "VIEW"; // subContext

	CONTEXT_TYPE_UNSPECIFIED = "UNSPECIFIED";
	CONTEXT_TYPE_SEARCH = "SEARCH";
	CONTEXT_TYPE_STORELIST = "STORELIST";
	CONTEXT_TYPE_OPEN_STORELIST = "OPEN_STORELIST"; // subContext

	CONTEXT_TYPE_ORDERS = "ORDERS";
	SUB_CONTEXT_TYPE_ORDERDETAILS = "ORDER_DETAILS";
	NEW_CONTEXT_PAGES_LIST: string[] = [
		// there shall be no order history pages here, otherewise our orderhistory context would be broken
		"category",
		"shoppingList",
		"metroList",
		"empStart",
		"checkout:order-entry", // bui: customer shop view""
		"empCustomerSearch",
		"empStoreListManagement", // bui: back office
		"campaignManagement",
		"campaignAnalytics",
		"categoryImagesSettings",
		"storeActivation", // bui: activation
	];
	// these are indepent of a specific order:
	ORDER_HISTORY_OVERVIEW_PAGE_LIST: string[] = [
		"customerPortalMyOrdersAll",
		"customerPortalMyOrdersDelivered",
		"customerPortalMyOrdersOpen",
		"customerPortalMyOrdersCancelled",
	];
	// these are tied to one order (or suborder):
	ORDER_HISTORY_DETAILS_PAGE_LIST: string[] = [
		"orderHistoryDetail",
		"reorder",
		"submitMessagePage",
		"rateMerchantPage",
	];

	constructor() {
		this.resetState();
	}

	resetState() {
		this.state = this.getEmptyState();
	}

	getEmptyState(): State {
		return {
			contextId: liteguid.create(),
			contextType: this.CONTEXT_TYPE_UNSPECIFIED,
			subContextId: liteguid.create(),
			subContextType: this.CONTEXT_TYPE_UNSPECIFIED,
			testGroups: {},
			cookieDomain: document.location.hostname,
			userCookieName: "ms_user",
			sessionCookieName: "ms_session",
			orderId: null,
		};
	}

	createNewContextIfRequired(eventType: string, structuredData: any): void {
		if (eventType === "search.trigger") {
			this.handleSearchTriggerEvent(structuredData);
		} else if (eventType === "search.filter") {
			this.handleSearchFilterEvent();
		} else if (eventType === "storelists.management.search.openList") {
			this.handleStoreListManagementOpenListEvent();
		} else if (eventType === "page") {
			this.handlePageEvent(structuredData);
		}
	}

	handleSearchTriggerEvent(structuredData: any): void {
		const isInitialSearch = structuredData?.tags?.initialSearch ?? false;

		if (this.state.contextType !== this.CONTEXT_TYPE_SEARCH || isInitialSearch) {
			this.createNewContext(this.CONTEXT_TYPE_SEARCH);
		}
	}

	handleSearchFilterEvent(): void {
		this.createNewSubContext(this.CONTEXT_TYPE_VIEW);
	}

	handleStoreListManagementOpenListEvent(): void {
		this.createNewSubContext(this.CONTEXT_TYPE_OPEN_STORELIST);
	}

	handlePageEvent(structuredData: any): void {
		const pageName = structuredData?.tags?.pageName;
		const validPage =
			this.NEW_CONTEXT_PAGES_LIST.includes(pageName) ||
			this.ORDER_HISTORY_OVERVIEW_PAGE_LIST.includes(pageName) ||
			this.ORDER_HISTORY_DETAILS_PAGE_LIST.includes(pageName);

		if (validPage) {
			this.createCorrectPageContext(pageName, structuredData);
		}
	}

	createCorrectPageContext(pageName: string, structuredData: any) {
		const isOrderOverview = this.ORDER_HISTORY_OVERVIEW_PAGE_LIST.includes(pageName);
		const isOrderDetail = this.ORDER_HISTORY_DETAILS_PAGE_LIST.includes(pageName);

		if (isOrderDetail || isOrderOverview) {
			this.createOrderContext(pageName, structuredData, isOrderOverview);
		} else if (this.isStoreListNotJustMetroList(pageName, structuredData)) {
			// only create a new storelist context if we are not already in one.
			// with this, filters, etc. will not create a new storelist context every time
			if (this.getContextType() !== this.CONTEXT_TYPE_STORELIST) {
				this.createNewContext(this.CONTEXT_TYPE_STORELIST);
			}
		} else {
			this.createNewContext(pageName.toUpperCase());
		}
	}

	createOrderContext(pageName: string, structuredData: any, isOrderOverview: boolean) {
		if (isOrderOverview) {
			if (this.state.contextType !== this.CONTEXT_TYPE_ORDERS) {
				// context switch:
				this.createNewContext(this.CONTEXT_TYPE_ORDERS);
			}
		} else {
			const oldOrderId = this.state.orderId;
			const newOrderId = structuredData?.tags?.orderId;

			if (!newOrderId) {
				// the event does not have the required id,
				// not much we can do about that here :/
				return;
			}

			//  subcontext != startswith(structuredData.orderId)
			const contextWasOrderAlready = this.state.contextType === this.CONTEXT_TYPE_ORDERS;

			if (oldOrderId === newOrderId) {
				// do nothing
				return;
			}

			this.state.orderId = newOrderId;

			if (contextWasOrderAlready) {
				// order id has changed, set sub context:
				this.createNewSubContext(this.SUB_CONTEXT_TYPE_ORDERDETAILS);
				return;
			}

			// everything has changed, new context is necessary
			this.createNewContext(this.CONTEXT_TYPE_ORDERS, this.SUB_CONTEXT_TYPE_ORDERDETAILS);
		}
	}

	isStoreListNotJustMetroList(pageName: string, structuredData: any): boolean {
		return pageName === "metroList" && !!structuredData?.tags?.sourcePath?.includes("storelist");
	}

	createNewSubContext(subContextType: string) {
		this.state.subContextId = liteguid.create();
		this.state.subContextType = subContextType;
	}

	createNewContext(contextType: string, subContextType: string = this.CONTEXT_TYPE_VIEW) {
		this.state.contextId = liteguid.create();
		this.state.contextType = contextType;
		this.createNewSubContext(subContextType);
	}

	getContextId(): string {
		return this.state.contextId;
	}

	getContextType(): string {
		return this.state.contextType;
	}

	getSubContextId(): string {
		return this.state.subContextId;
	}

	getSubContextType(): string {
		return this.state.subContextType;
	}

	getCookieValue(name: string): string {
		const nameLen = name.length;
		const parts = document.cookie.split(";").map(each => each.trim());

		for (let pos = 0; pos < parts.length; pos++) {
			if (parts[pos].substring(0, nameLen + 1) === name + "=") {
				const partValue = parts[pos].substring(nameLen + 1);

				if (partValue && partValue.length > 0) {
					return partValue;
				}
			}
		}

		return "";
	}

	setCookieValue(name: string, valueString: string) {
		const cookieString = name + "=" + valueString + ";domain=" + this.state.cookieDomain + ";path=/";
		document.cookie = cookieString;
	}

	getOrCreateCookieWithUuid(name: string, create: boolean, optSession: boolean): string {
		const cVal = this.getCookieValue(name);

		if (cVal.length === 36) {
			return cVal;
		}

		if (!create) {
			return "";
		}

		const id = liteguid.create();
		let newValue = id;

		if (!optSession) {
			const now = new Date();
			now.setFullYear(now.getFullYear() + 10);
			newValue += "; expires=" + now.toUTCString();
		} else {
			newValue += ";";
		}

		this.setCookieValue(name, newValue);
		return id;
	}

	userUUID(): string {
		return this.getOrCreateCookieWithUuid(this.state.userCookieName, true, false);
	}

	sessionUUID(): string {
		return this.getOrCreateCookieWithUuid(this.state.sessionCookieName, true, true);
	}

	setTestGroups(params: Record<string, any>) {
		Object.assign(this.state.testGroups, params);
	}

	getTestGroups(): any {
		return this.state.testGroups;
	}

	getState(): Record<string, any> {
		return { ...this.state };
	}
}

export default new TrackingState() as TrackingState;
